"use client";

import React, { useEffect, useState } from "react";
import { ArrowRight, Layers, RefreshCw, Funnel as Filter, Info, ChevronRight, SlidersHorizontal, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
interface ScreenTypeItem {
  id: string;
  titleEn: string;
  titleZh: string;
  titleDe: string;
  categoryEn: string;
  categoryZh: string;
  categoryDe: string;
  descriptionEn: string;
  descriptionZh: string;
  iconName: string;
  to: string;
  imgSrc: string;
  imgSrcset: string;
  imgSizes: string;
  specAperture: string;
  specProfileEn: string;
  specProfileZh: string;
  specApplicationEn: string;
  specApplicationZh: string;
}
const screenTypes: ScreenTypeItem[] = [{
  id: "flat-panel",
  titleEn: "Flat Panel Screens",
  titleZh: "平板筛网",
  titleDe: "Flachsiebböden",
  categoryEn: "Planar Profiles",
  categoryZh: "平面型材",
  categoryDe: "Flachpaneele",
  descriptionEn: "Planar profile-wire screens for vibratory decks, static classification, and heavy dewatering operations.",
  descriptionZh: "用于振动筛面、静态分级以及重载脱水作业的平面楔形丝筛网板。",
  iconName: "Layers",
  to: "/FlatPanelScreensPage",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/flat_panel_screens_overview.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/flat_panel_screens_overview.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/flat_panel_screens_overview.png-wvc-sizes",
  specAperture: "0.05 mm – 10.0 mm",
  specProfileEn: "Standard & heavy-duty V-wire",
  specProfileZh: "标准型及重型 V 型丝",
  specApplicationEn: "Vibratory decks, stationary tables",
  specApplicationZh: "振动筛板、静态分选台"
}, {
  id: "curved-screen",
  titleEn: "Curved Screens",
  titleZh: "弧形筛网",
  titleDe: "Bogensiebe & Krümmer",
  categoryEn: "Radial Profiles",
  categoryZh: "径向弧形型材",
  categoryDe: "Bogenelemente",
  descriptionEn: "Curved profile-wire panels engineered for cross-flow dewatering, slurry thickening, and pulp recovery.",
  descriptionZh: "专为错流脱水、矿浆浓缩和纸浆回收设计的弧形楔形丝筛板。",
  iconName: "RefreshCw",
  to: "/CurvedScreensPage",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/curved_screens_overview.png",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/curved_screens_overview.png-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/curved_screens_overview.png-wvc-sizes",
  specAperture: "0.10 mm – 6.0 mm",
  specProfileEn: "Cross-flow oriented wedge wire",
  specProfileZh: "错流定向楔形丝",
  specApplicationEn: "Mineral slurries, pulp thickening",
  specApplicationZh: "矿物矿浆、纸浆浓缩"
}, {
  id: "sieve-bend",
  titleEn: "Sieve Bend Screens",
  titleZh: "弧形筛板 (压力筛曲面筛)",
  titleDe: "Spaltsiebbögen",
  categoryEn: "Gravity Separation",
  categoryZh: "重力分离",
  categoryDe: "Schwerkrafttrennung",
  descriptionEn: "Static curved sieve bend screens with parabolic contours for high-capacity gravity liquid-solid separation.",
  descriptionZh: "具有抛物线曲面的静态弧形筛板，适用于大通量重力固液分离。",
  iconName: "Filter",
  to: "/SieveBendScreensPage",
  imgSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screens_overview.webp",
  imgSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screens_overview.webp-wvc-srcset",
  imgSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/sieve_bend_screens_overview.webp-wvc-sizes",
  specAperture: "0.05 mm – 5.0 mm",
  specProfileEn: "Precision continuous slot wire",
  specProfileZh: "精密连续缝隙筛丝",
  specApplicationEn: "Effluent treatment, starch dewatering",
  specApplicationZh: "污水处理、淀粉脱水"
}];
export default function ScreenTypes() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedSpecId, setSelectedSpecId] = useState<string>("flat-panel");
  const [lang, setLang] = useState<"en" | "zh">("en");
  useEffect(() => {
    try {
      const savedLang = localStorage.getItem("mf_lang");
      if (savedLang === "zh" || savedLang === "en") {
        setLang(savedLang);
      }
    } catch {
      // Ignore localStorage errors
    }
    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang?: "en" | "zh";
      }>;
      if (customEvent.detail?.lang) {
        setLang(customEvent.detail.lang);
      } else {
        const current = localStorage.getItem("mf_lang");
        if (current === "zh" || current === "en") {
          setLang(current);
        }
      }
    };
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "mf_lang" && (e.newValue === "zh" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("mf-language-change", handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("mf-language-change", handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  const isZh = lang === "zh";
  const getIcon = (name: string) => {
    switch (name) {
      case "Layers":
        return <Layers className="size-5 text-primary" aria-hidden="true" />;
      case "RefreshCw":
        return <RefreshCw className="size-5 text-primary" aria-hidden="true" />;
      case "Filter":
        return <Filter className="size-5 text-primary" aria-hidden="true" />;
      default:
        return <Layers className="size-5 text-primary" aria-hidden="true" />;
    }
  };
  return <section id="screentypesoverview" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs uppercase tracking-wider text-muted-foreground font-mono bg-card">
              {isZh ? "产品配置总览 / Produktübersicht" : "Product Overview / Produktübersicht"}
            </Badge>
            <span className="text-xs text-muted-foreground font-mono">DIN / ISO 9044</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-4 font-default">
            {isZh ? "约翰逊楔形筛网配置与规格" : "Wedge wire screen configurations"}
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            {isZh ? "为您的分离与分级设备选择量身定制的筛网截面几何结构。支持 304、316L 及双相不锈钢材质，定制缝隙孔径与结构加强筋设计。" : "Select the screen profile geometry engineered for your separation and classification equipment. Custom slot apertures and structural reinforcements fabricated in 304, 316L, and duplex stainless alloys."}
          </p>
        </div>

        {/* 3-Column Card Grid (Use-Case & Geometry Overview) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {screenTypes.map((item, index) => {
          const cardMotionProps = shouldReduceMotion ? {} : {
            initial: {
              opacity: 0,
              y: 20
            },
            whileInView: {
              opacity: 1,
              y: 0
            },
            viewport: {
              once: true,
              amount: 0.2
            },
            transition: {
              duration: 0.45,
              delay: index * 0.1,
              ease: "easeOut"
            }
          };
          return <motion.div key={item.id} data-index={index} {...cardMotionProps} className="flex flex-col h-full">
                <Card className="flex flex-col justify-between h-full bg-card text-card-foreground border border-border shadow-sm hover:shadow-md hover:border-primary/40 transition-[border-color,box-shadow] duration-200 overflow-hidden">
                  <div>
                    {/* Media Preview Container */}
                    <div className="relative aspect-16/10 w-full bg-muted overflow-hidden border-b border-border">
                      <img src={item.imgSrc} data-wvc-srcset={item.imgSrcset} data-wvc-sizes={item.imgSizes} alt={isZh ? `${item.titleZh} - 工业楔形丝筛网` : `${item.titleEn} - industrial wedge wire screen`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                      <div className="absolute top-3 left-3 flex gap-1.5 flex-wrap">
                        <Badge variant="secondary" className="backdrop-blur-xs bg-secondary/90 text-xs font-mono">
                          {isZh ? item.categoryZh : item.categoryEn}
                        </Badge>
                        <Badge variant="outline" className="backdrop-blur-xs bg-background/80 text-[11px] text-muted-foreground font-mono">
                          {item.categoryDe}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-3">
                      <div className="flex items-center gap-2.5 mb-2">
                        <div className="p-2 rounded-md bg-primary/10 flex items-center justify-center">
                          {getIcon(item.iconName)}
                        </div>
                        <div>
                          <CardTitle className="text-xl font-semibold text-foreground">
                            {isZh ? item.titleZh : item.titleEn}
                          </CardTitle>
                          <span className="text-xs font-medium text-muted-foreground italic">
                            {item.titleDe}
                          </span>
                        </div>
                      </div>
                      <CardDescription className="text-sm text-muted-foreground pt-1 leading-normal">
                        {isZh ? item.descriptionZh : item.descriptionEn}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="space-y-3 pt-1">
                      <Separator className="my-2" />
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between items-center text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {isZh ? "缝隙孔径 / Spaltweite:" : "Slot Opening / Spaltweite:"}
                          </span>
                          <span className="font-mono text-foreground font-semibold">{item.specAperture}</span>
                        </div>
                        <div className="flex justify-between items-center text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {isZh ? "筛丝截面 / Profil:" : "Wire Geometry / Profil:"}
                          </span>
                          <span className="truncate max-w-[150px] text-right">
                            {isZh ? item.specProfileZh : item.specProfileEn}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-muted-foreground">
                          <span className="font-medium text-foreground">
                            {isZh ? "典型应用 / Einsatz:" : "Typical Process / Einsatz:"}
                          </span>
                          <span className="truncate max-w-[150px] text-right">
                            {isZh ? item.specApplicationZh : item.specApplicationEn}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 border-t border-border mt-auto bg-muted/20">
                    <Button asChild variant="outline" className="w-full justify-between group text-sm font-medium hover:bg-primary hover:text-primary-foreground transition-colors bg-card">
                      <Link to={item.to}>
                        <span>{isZh ? `探索 ${item.titleZh}` : `Explore ${item.titleEn}`}</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Technical Specification Table Pattern */}
        <div className="space-y-4 mb-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <SlidersHorizontal className="size-4 text-primary" />
              <h3 className="text-lg font-semibold text-foreground font-default">
                {isZh ? "技术参数矩阵与尺寸规格" : "Technical Specification Matrix & Dimensional Parameters"}
              </h3>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground font-mono">
              <span className="inline-block size-2 rounded-lg bg-chart-4" />
              <span>{isZh ? "标准及定制公差 (ISO 9044)" : "Standard & Custom Tolerances (ISO 9044)"}</span>
            </div>
          </div>

          <div className="border border-border rounded-lg overflow-x-auto bg-card shadow-xs">
            <table className="w-full text-left text-sm border-collapse min-w-[700px]">
              <thead>
                <tr className="border-b border-border bg-muted/60 text-xs font-mono uppercase text-muted-foreground">
                  <th className="py-3.5 px-4 font-semibold text-foreground">
                    {isZh ? "配置结构 / Ausführung" : "Configuration / Ausführung"}
                  </th>
                  <th className="py-3.5 px-4 font-semibold text-foreground">
                    {isZh ? "型材类别 / Profiltyp" : "Category / Profiltyp"}
                  </th>
                  <th className="py-3.5 px-4 font-semibold text-foreground">
                    {isZh ? "缝隙孔径 / Spaltweite" : "Slot Opening / Spaltweite"}
                  </th>
                  <th className="py-3.5 px-4 font-semibold text-foreground">
                    {isZh ? "筛丝截面 / Profil" : "Wire Geometry / Profil"}
                  </th>
                  <th className="py-3.5 px-4 font-semibold text-foreground">
                    {isZh ? "典型工艺 / Einsatz" : "Typical Process / Einsatz"}
                  </th>
                  <th className="py-3.5 px-4 font-semibold text-foreground text-right">
                    {isZh ? "技术数据表" : "Datasheet Link"}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {screenTypes.map((row, i) => {
                const isSelected = selectedSpecId === row.id;
                return <tr key={row.id} data-index={i} onClick={() => setSelectedSpecId(row.id)} className={`cursor-pointer transition-colors ${isSelected ? "bg-accent/40" : "hover:bg-muted/30"}`}>
                      <td className="py-3.5 px-4 font-medium text-foreground">
                        <div className="flex items-center gap-2">
                          <span className={`size-1.5 rounded-lg ${isSelected ? "bg-primary" : "bg-muted-foreground/40"}`} />
                          <div>
                            <span className="font-semibold block">{isZh ? row.titleZh : row.titleEn}</span>
                            <span className="text-xs text-muted-foreground font-sans italic">{row.titleDe}</span>
                          </div>
                        </div>
                      </td>
                      <td className="py-3.5 px-4">
                        <Badge variant="outline" className="text-xs font-mono bg-card">
                          {isZh ? row.categoryZh : row.categoryEn}
                        </Badge>
                      </td>
                      <td className="py-3.5 px-4 font-mono font-semibold text-primary">
                        {row.specAperture}
                      </td>
                      <td className="py-3.5 px-4 text-xs text-foreground font-medium">
                        {isZh ? row.specProfileZh : row.specProfileEn}
                      </td>
                      <td className="py-3.5 px-4 text-xs text-muted-foreground">
                        {isZh ? row.specApplicationZh : row.specApplicationEn}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <Button asChild variant="ghost" size="sm" className="h-8 gap-1 text-xs font-medium text-primary hover:text-primary">
                          <Link to={row.to}>
                            <span>{isZh ? "详情" : "Details"}</span>
                            <ChevronRight className="size-3.5" />
                          </Link>
                        </Button>
                      </td>
                    </tr>;
              })}
              </tbody>
            </table>

            {/* Table Micro-Summary Footer */}
            <div className="p-3.5 bg-muted/20 border-t border-border flex flex-wrap items-center justify-between text-xs text-muted-foreground gap-3">
              <div className="flex items-center gap-1.5">
                <Info className="size-3.5 text-primary shrink-0" />
                <span>
                  {isZh ? "所有楔形丝配置均可采用 AISI 304 (1.4301)、AISI 316L (1.4404) 及特种耐腐蚀合金制造。" : "All wedge wire configurations are available in AISI 304 (1.4301), AISI 316L (1.4404), and specialty corrosion alloys."}
                </span>
              </div>
              <div className="flex items-center gap-1 text-xs font-medium text-primary">
                <CheckCircle2 className="size-3.5 text-primary" />
                <span>{isZh ? "符合 EN 10204 3.1 质量认证" : "EN 10204 3.1 Certified"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Technical Note & Quote CTA */}
        <div className="p-4 md:p-6 rounded-lg bg-card border border-border shadow-xs flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">
              {isZh ? "定制外框、背部支撑扁钢与端部压边紧固系统" : "Custom framing, back-support bars, and end-clamping systems"}
            </p>
            <p className="text-xs text-muted-foreground">
              {isZh ? "根据 OEM 图纸尺寸精密制造，采用激光校准缝隙，提供 AISI 304 (1.4301)、316L (1.4404) 及哈氏合金材质。" : "Manufactured to OEM draw dimensions with precision laser-aligned slots in AISI 304 (1.4301), 316L (1.4404), and Hastelloy."}
            </p>
          </div>
          <Button asChild size="sm" variant="default" className="shrink-0 font-medium">
            <Link to="/RequestAQuotePage">
              {isZh ? "索取技术数据表" : "Request Technical Datasheet"}
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}